import React, { useEffect, useMemo, useState } from 'react'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as IconWarningOutline } from '@toasttab/shared/src/assets/svg/icon-warning-outline-black.svg'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIcon } from '@toasttab/shared/src/assets/svg/icon-close.svg'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import Sites from '@toasttab/shared/src/data/Sites.json'
import { normalizeRegion } from '@toasttab/shared/src/utils/siteUtils'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'

interface RegionSwitcherInfoBannerProps {
  packageName: string
  siteId: number
  localizedEntries?: {
    siteId: number
    language: string
    slug?: string
    slugOverride?: string
    disabledRegionalSwitcherModal?: boolean
  }[]
}

const normalizeLang = (lang = '') => lang.toLowerCase().split('-')[0]
const normalizeCountryCode = (lang = '') =>
  lang.toLowerCase().split('-')[1] || lang.toLowerCase()

const RegionSwitcherInfoBanner: React.FC<RegionSwitcherInfoBannerProps> = ({
  packageName,
  siteId,
  localizedEntries = []
}) => {
  const { headerHeight, setActiveRegionSwitcherInfoBanner, setHeaderHeight } =
    usePageContext()
  const { t } = useI18next()
  const [isUserCountrySupported, setIsUserCountrySupported] = useState(false)
  const [isUserLangSupported, setIsUserLangSupported] = useState(false)
  const [showRegionBanner, setShowRegionBanner] = useState(false)
  const [regionData, setRegionData] = useState({
    countryCode: '',
    regionPreference: ''
  })
  const { geoData, loading } = useGeoLocation()

  const browserLang = useMemo(
    () =>
      typeof navigator === 'undefined'
        ? 'en'
        : navigator.language?.split('-')[0]?.toLowerCase() || 'en',
    []
  )

  const activeSite = useMemo(
    () => Sites.sites.find((site) => site.siteId === siteId),
    [siteId]
  )

  useEffect(() => {
    if (geoData) {
      setRegionData({
        countryCode: geoData.country.iso_code,
        regionPreference: geoData.regionPreference.iso_code
      })
    }
  }, [geoData, loading])

  useEffect(() => {
    const userLangCookie = _helpers.getCookie('_userLanguage')
    if (!userLangCookie && browserLang) {
      _helpers.createCookie('_userLanguage', browserLang, 365, '/')
    }
  }, [browserLang])

  useEffect(() => {
    if (
      !regionData.countryCode ||
      localizedEntries.some((entry: any) => entry.disabledRegionalSwitcherModal)
    ) {
      setActiveRegionSwitcherInfoBanner(false)
      return
    }

    const userLangCookie = _helpers.getCookie('_userLanguage')
    const userCountry = regionData.countryCode?.toUpperCase()
    const preferredRegion = regionData.regionPreference?.toUpperCase()

    const doesRegionHaveSupportedContent = localizedEntries.some((entry: any) =>
      Sites.sites.some(
        (site) =>
          site.siteId === entry.siteId &&
          normalizeRegion(site.countryCode) ===
            normalizeRegion(preferredRegion || userCountry)
      )
    )

    const doesLanguageHaveSupportedContent = localizedEntries.some(
      (entry: any) =>
        normalizeLang(entry.language) === normalizeLang(userLangCookie) &&
        (normalizeCountryCode(entry.language) ===
          normalizeCountryCode(userLangCookie) ||
          normalizeCountryCode(entry.language) ===
            normalizeCountryCode(userCountry))
    )
    setIsUserCountrySupported(doesRegionHaveSupportedContent)
    setIsUserLangSupported(doesLanguageHaveSupportedContent)

    const showBanner =
      (!doesLanguageHaveSupportedContent || !doesRegionHaveSupportedContent) &&
      sessionStorage.getItem('regionBannerClosed') !== 'true'

    setShowRegionBanner(showBanner)
    setActiveRegionSwitcherInfoBanner(showBanner)
  }, [
    regionData,
    activeSite,
    localizedEntries,
    geoData,
    setActiveRegionSwitcherInfoBanner
  ])

  const supportedRegionOrLocaleSite = useMemo(() => {
    if (!regionData.countryCode) return null
    return Sites.sites.find(
      (site) =>
        site.countryCode === regionData.countryCode.toUpperCase() &&
        site.siteId !== siteId // Exclude current site
    )
  }, [regionData.countryCode, siteId])

  if (!geoData || !localizedEntries.length) return null

  const renderBannerMessage = () => {
    if (!isUserCountrySupported && supportedRegionOrLocaleSite) {
      return (
        <span data-testid='unsupportedCountryWithRegionHomeBannerMessage'>
          {t('unsupportedCountryWithRegionHomeBannerMessage', {
            region: supportedRegionOrLocaleSite.siteName,
            defaultValue: `The content is not available in ${supportedRegionOrLocaleSite.siteName}. `
          })}
          <a
            className='underline font-normal text-blue-700 text-sm ml-1'
            data-testid='backToRegionHomeLink'
            href={supportedRegionOrLocaleSite.url}
          >
            {t('backToRegionHome', {
              region: supportedRegionOrLocaleSite.siteName,
              defaultValue: `Back to ${supportedRegionOrLocaleSite.siteName} home.`
            })}
          </a>
        </span>
      )
    }
    if (!isUserCountrySupported) {
      return (
        <span data-testid='unsupportedCountryBannerMessage'>
          {t('unsupportedCountryBannerMessage', {
            countryCode: `${activeSite?.siteName}`,
            defaultValue: `This content isn't available in your region. You're viewing the ${activeSite?.siteName} version of this page.`
          })}
        </span>
      )
    }
    if (!isUserLangSupported) {
      return (
        <span data-testid='unsupportedLanguageBannerMessage'>
          {t('unsupportedLanguageBannerMessage', {
            language: t(`${activeSite?.languageName}`),
            defaultValue: `This page is not available in your preferred language. Showing ${activeSite?.languageName} instead.`
          })}
        </span>
      )
    }
  }

  const handleClose = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    event.preventDefault()
    setShowRegionBanner(false)
    setActiveRegionSwitcherInfoBanner(false)
    sessionStorage.setItem('regionBannerClosed', 'true')

    // Re-set headerHeight so the mobile nav positioning adjusts when the banner is closed
    setTimeout(() => {
      const headHeight =
        typeof document !== 'undefined'
          ? document.querySelector('.headerContent')?.clientHeight
          : 0
      if (headHeight > 0 && headHeight !== headerHeight) {
        setHeaderHeight(headHeight)
      }
    }, 100)
  }
  return (
    <>
      {showRegionBanner ? (
        <div
          className='soverflow-hidden'
          data-testid='RegionSwitcherInfoBanner'
        >
          <div className='relative bg-blue-0 md:h-10 min-h-[40px] text-base'>
            <div className='py-2 pl-4 md:pl-0 md:flex md:justify-center md:items-center md:h-full'>
              <div className='flex justify-start md:justify-center items-center h-full text-left md:text-center md:items-start'>
                <div className='flex items-start w-full flex-col md:flex-row md:justify-center md:items-center pr-10'>
                  <div className='flex items-start w-full md:justify-center gap-1'>
                    <span className='font-sansPro font-normal text-secondary text-sm tracking-[.2px]'>
                      {renderBannerMessage()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button className='absolute top-1 right-1' onClick={handleClose}>
              <CloseIcon height='30' width='30' />
            </button>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default RegionSwitcherInfoBanner
